import React, { useEffect, useState } from 'react';
import { useLocalizationContext, useMbm } from '@amzn/react-arb-tools';
import { buildLocalizationContext, DEFAULT_BUNDLE } from 'src/i18n';
import BundleFailureNotice from 'src/i18n/BundleFailureNotice';
import DefaultBundleContext, {
  TranslationBundle,
} from 'src/i18n/shim/DefaultBundleContext';

/** Resource probed to tell a real bundle from the empty one arb-tools resolves on a failed fetch.
 * Any always-present id would do; the header renders on every route, so this one cannot be dropped
 * without the probe being revisited, and a test asserts `DEFAULT_BUNDLE` still carries it. */
export const SENTINEL_RESOURCE_ID = 'app-header-home-aria';

const isUsableBundle = (bundle: TranslationBundle): boolean => {
  try {
    return typeof bundle.getMessage(SENTINEL_RESOURCE_ID) === 'string';
  } catch {
    // arb-tools throws `Resource does not exist` off an empty bundle; that IS the failure signal.
    return false;
  }
};

interface Props {
  readonly children?: React.ReactNode;
  readonly fallback?: React.ReactNode;
}

/**
 * Resolves the default bundle and publishes it, so components get a synchronous `t()` instead of
 * each branching on its own loading flag. Three invariants, explained in full by the failure-mode
 * table in `.agents/planning/2026-10-12-panther-localization/design/design.md` §6:
 *
 * 1. Load via `useMbm`, not `useBundle`, which clears its bundle first and would remount the whole
 *    app on every locale change, discarding component state.
 * 2. Probe for {@link SENTINEL_RESOURCE_ID} before publishing: `getBundle` resolves an *empty*
 *    bundle rather than rejecting on an unfetchable pack, which would otherwise surface as a throw
 *    from the first `t()` during render. An empty bundle means the whole fallback chain failed, so
 *    there is no retry — re-resolving re-walks the same memoized failures.
 * 3. Leave the context locale at the one the user asked for, so `lang`/`dir` and every `Intl.*`
 *    call still follow it while copy falls back to English. Forcing it to the source locale would
 *    flip text direction for an RTL reader whose only problem is a stale copy pack.
 *
 * The locale comes from the React context, not the manager's own `localizationContextProvider`,
 * which reads persisted storage: that would make a switch depend on the write landing before the
 * re-render, and break entirely where storage is unavailable.
 */
const DefaultBundleProvider: React.FC<Props> = ({ children, fallback }) => {
  const mbm = useMbm();
  const { localizationContext } = useLocalizationContext();
  const [bundle, setBundle] = useState<TranslationBundle | undefined>();
  const [failed, setFailed] = useState(false);
  const locale = localizationContext.getLocale();

  useEffect(() => {
    let cancelled = false;
    // Cleared per attempt so switching to a locale whose pack IS available recovers, rather than
    // latching the notice for the rest of the session.
    setFailed(false);

    void (async () => {
      // Rebuilt from the locale string rather than closing over `localizationContext`, whose
      // identity changes on every render of the provider above and as a dependency would
      // re-resolve in a loop. Revisit if that provider ever sets a time zone or i18nDomain.
      const resolve = async (forLocale: string): Promise<TranslationBundle> =>
        (await mbm.getBundle(
          DEFAULT_BUNDLE,
          buildLocalizationContext(forLocale),
        )) as TranslationBundle;

      try {
        const resolved = await resolve(locale);

        // Guards a late resolution from a superseded locale overwriting a newer one, and a set
        // after unmount.
        if (cancelled) {
          return;
        }
        if (isUsableBundle(resolved)) {
          setBundle(resolved);
        } else {
          setFailed(true);
        }
      } catch {
        // Covers the undocumented rejection the probe above cannot see — a throw from
        // `resolveResourcePack`, a malformed pack, a future arb-tools that rejects. Without it
        // neither setter runs and the provider sits on `fallback` forever: a blank page, the one
        // outcome BundleFailureNotice exists to prevent.
        if (!cancelled) {
          setFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mbm, locale]);

  if (failed) {
    return <BundleFailureNotice />;
  }

  if (!bundle) {
    return <>{fallback ?? null}</>;
  }

  return (
    <DefaultBundleContext.Provider value={bundle}>
      {children}
    </DefaultBundleContext.Provider>
  );
};

export default DefaultBundleProvider;
